import React, { useEffect, useState, useCallback } from 'react';
import clsx from 'clsx';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { LuPlay } from 'react-icons/lu';
import {
  PhonicsLesson,
  PhonicsLiveLessonOnboardingStatus,
  PhonicsStudentLiveStatus,
  PicturePasswordItemFragment,
} from '@/api/types';
import { CdnObjectType, SymbolType } from '@/types';
import { useAuth } from '@/context/Authentication';
import { useLiveLesson } from '@/context/LiveLesson';
import { Panel } from '../Panel';
import { Form } from '../Form';
import { TextInput } from '../TextInput';
import { CdnMedia } from '../CdnMedia';
import { Image } from '../Image';
import { Spinner } from '../Spinner';
import { ErrorHippo } from '../ErrorHippo';
import { RoundNextButton } from '../buttons/RoundNextButton';
import { PicturePasswordInput } from '../PicturePasswordInput';
import { TextPasswordInput } from '../TextPasswordInput';

const PASSCODE_LENGTH = 3;

interface InputAccessCodeProps {
  accessCode: string | null;
  setAccessCode: (value: string | null) => void;
  onSubmit: () => void;
  loading?: boolean;
  error?: string;
}

const InputAccessCode = ({
  accessCode,
  setAccessCode,
  onSubmit,
  loading = false,
  error,
}: InputAccessCodeProps) => (
  <>
    <Form
      className="flex justify-center gap-6 my-6"
      onSubmit={() => onSubmit()}
    >
      <div className="flex items-center">
        <TextInput
          name="accessCode"
          placeholder="Class code"
          value={accessCode}
          onChange={setAccessCode}
          className={clsx(
            'text-3xl w-[400px] border-blue-normal',
            'border-4 rounded-xl px-6 py-4',
          )}
          disabled={loading}
        />
      </div>

      <RoundNextButton kind="submit" size="sm" color="green" />
    </Form>

    {/* <a> to prevent next adding the /phonics prefix */}
    <a className="block text-center hover:underline" href="/login">
      I&apos;d like to login with a username and password
    </a>

    {loading && (
      <div className="flex justify-center">
        <Spinner size="3xl" />
      </div>
    )}

    {error && <div className="flex justify-center">{error}</div>}
  </>
);

interface ChooseUserProps {
  onboardingStatus: PhonicsLiveLessonOnboardingStatus;
  userStatus?: PhonicsStudentLiveStatus;
  setUserStatus: (value: PhonicsStudentLiveStatus | undefined) => void;
}

const ChooseUser = ({
  onboardingStatus,
  userStatus,
  setUserStatus,
}: ChooseUserProps) => {
  const avatars = [
    'apple',
    'camel',
    'cat',
    'chicken',
    'cow',
    'dog',
    'elephant',
    'hamster',
    'monkey',
    'octopus',
    'rabbit',
    'raccoon',
  ];

  return (
    <div className="flex flex-wrap justify-center">
      {onboardingStatus.students?.map((student, i) => (
        <div
          key={student.id}
          className="flex flex-col w-64 my-12 xl:mx-12 text-center"
        >
          <button
            className={clsx(
              'flex w-32 h-32 mx-auto mb-6 bg-white',
              'hover:bg-neutral-100 rounded-full text-center',
              'items-center justify-center',
              userStatus === student
                ? 'border-4 border-green-normal'
                : 'border-2 border-neutral-200',
            )}
            onClick={() => setUserStatus(student)}
          >
            <Image
              src={`/images/avatars/${avatars[i % avatars.length]}.png`}
              width={64}
              height={64}
              alt={student.name}
            />
          </button>
          <span className="text-2xl">{student.name}</span>
        </div>
      ))}
    </div>
  );
};

interface InputPasscodeProps {
  choose?: boolean;
  userStatus: PhonicsStudentLiveStatus;
  passcode: Array<PicturePasswordItemFragment>;
  setPasscode: (value: Array<PicturePasswordItemFragment>) => void;
  password: string;
  setPassword: (value: string) => void;
  onSubmit: () => void;
  loading: boolean;
  error?: string;
}

const InputPasscode = ({
  choose = false,
  userStatus,
  passcode,
  setPasscode,
  password,
  setPassword,
  onSubmit,
  loading = false,
  error,
}: InputPasscodeProps) => {
  const [passwordType, setPasswordType] = useState<'picture' | 'text'>(
    'picture',
  );

  return (
    <>
      <h1 className="my-12 text-6xl text-center">Hi {userStatus?.name}!</h1>

      <h2 className="my-12 text-3xl text-center">
        {choose ? 'Choose your new password' : 'Enter your password'}
      </h2>

      <div className="flex flex-col justify-center">
        {passwordType === 'picture' ? (
          <>
            <PicturePasswordInput
              passcode={passcode}
              setPasscode={setPasscode}
              disabled={loading}
            />
            <button
              className="text-2xl text-center mt-12"
              onClick={() => setPasswordType('text')}
            >
              I&apos;d like to type in a password
            </button>
          </>
        ) : (
          <>
            <TextPasswordInput
              value={password}
              onChange={setPassword}
              disabled={loading}
              onSubmit={onSubmit}
            />

            <button
              className="text-2xl text-center mt-12"
              onClick={() => setPasswordType('picture')}
            >
              I&apos;d like to use my picture password
            </button>
          </>
        )}

        {loading && <Spinner size="xl" />}
      </div>

      {error && (
        <div className="flex justify-center items-center text-black dark:text-white text-2xl text-center">
          <p className="my-6">
            We couldn&apos;t log you in. Have you used the correct password?
          </p>

          <ErrorHippo className="w-64 h-auto my-6" />
        </div>
      )}
    </>
  );
};

interface ChooseActivityProps {
  currentLesson: PhonicsLesson;
}

const ChooseActivity = ({ currentLesson }: ChooseActivityProps) => (
  <div className="flex justify-center">
    <Link
      className="block w-64 lg:w-96 group text-center"
      href={`/play/${currentLesson.id}`}
    >
      <Panel className="flex flex-col gap-3 lg:gap-6 justify-center items-center w-64 h-64 lg:w-96 lg:h-96">
        <CdnMedia
          symbol={currentLesson?.sounds?.[0]?.name || ''}
          symbolType={SymbolType.Sound}
          objectType={CdnObjectType.Image}
          width={156}
          height={156}
        />

        <h2 className="text-6xl lg:text-8xl">
          {currentLesson?.sounds?.[0]?.name}
        </h2>
      </Panel>

      <span className="relative -top-3 inline-block px-12 py-4 bg-green-normal group-hover:bg-green-dark text-white text-2xl rounded-xl">
        <LuPlay />
      </span>
    </Link>
  </div>
);

export type LearnOnboardSteps =
  | 'accessCode'
  | 'chooseUser'
  | 'enterPasscode'
  | 'chooseActivity';

interface LearnOnboardProps {
  onChangeStep?: (step: LearnOnboardSteps) => void;
}

export const StudentOnboard = ({ onChangeStep }: LearnOnboardProps) => {
  const searchParams = useSearchParams();

  const { user } = useAuth();

  const {
    liveLesson,
    liveLessonOnboardingStatus,
    requestLiveLessonOnboardingStatus,
    liveLessonOnboardingStatusLoading,
    liveLessonOnboardingStatusError,
    liveLessonLogin,
    liveLessonLoginLoading,
    liveLessonLoginError,
    joinLiveLesson,
    liveLessonMembershipStatus,
  } = useLiveLesson();

  const [step, setStep] = useState<LearnOnboardSteps>('accessCode');

  const [accessCode, setAccessCode] = useState<string | null>(
    searchParams.get('accessCode'),
  );

  const [userStatus, setUserStatus] = useState<
    PhonicsStudentLiveStatus | undefined
  >();

  const [passcode, setPasscode] = useState<Array<PicturePasswordItemFragment>>(
    [],
  );

  const [password, setPassword] = useState<string>('');

  const handleSubmitWithPassword = useCallback(() => {
    console.log('handleSubmitWithPassword', password);
    if (!accessCode) {
      return;
    }

    if (!liveLessonOnboardingStatus) {
      return;
    }

    if (!userStatus) {
      return;
    }

    if (password) {
      liveLessonLogin(
        accessCode,
        liveLessonOnboardingStatus?.classId,
        userStatus.id,
        undefined,
        password,
      );
    }
  }, [accessCode, liveLessonOnboardingStatus, userStatus, password]);

  useEffect(() => {
    if (!accessCode) {
      return;
    }

    requestLiveLessonOnboardingStatus(accessCode);
  }, []);

  useEffect(() => {
    onChangeStep?.(step);
  }, [step, onChangeStep]);

  useEffect(() => {
    if (liveLessonOnboardingStatus) {
      if (!user) {
        setStep('chooseUser');
      }
    }
  }, [liveLessonOnboardingStatus, user]);

  useEffect(() => {
    if (userStatus && userStatus.isInitialised) {
      setStep('enterPasscode');
    }
  }, [userStatus]);

  useEffect(() => {
    if (!accessCode) {
      return;
    }

    if (!liveLessonOnboardingStatus) {
      return;
    }

    if (!userStatus) {
      return;
    }

    if (passcode.length === PASSCODE_LENGTH) {
      liveLessonLogin(
        accessCode,
        liveLessonOnboardingStatus?.classId,
        userStatus.id,
        passcode,
      );
    }
  }, [passcode, password, accessCode, liveLessonLogin, userStatus]);

  useEffect(() => {
    if (!user) {
      return;
    }
    if (!accessCode) {
      return;
    }
    if (!liveLessonOnboardingStatus) {
      return;
    }
    if (liveLessonMembershipStatus) {
      return;
    }

    joinLiveLesson(accessCode, liveLessonOnboardingStatus.classId);
  }, [
    user,
    accessCode,
    joinLiveLesson,
    liveLessonOnboardingStatus,
    liveLessonMembershipStatus,
  ]);

  useEffect(() => {
    if (!liveLessonMembershipStatus) {
      return;
    }

    setStep('chooseActivity');
  }, [liveLessonMembershipStatus]);

  return (
    <>
      {step === 'accessCode' && (
        <InputAccessCode
          accessCode={accessCode}
          setAccessCode={setAccessCode}
          onSubmit={() => {
            if (!accessCode) {
              return;
            }

            requestLiveLessonOnboardingStatus(accessCode);
          }}
          loading={liveLessonOnboardingStatusLoading}
          error={liveLessonOnboardingStatusError}
        />
      )}

      {step === 'chooseUser' && liveLessonOnboardingStatus && (
        <ChooseUser
          onboardingStatus={liveLessonOnboardingStatus}
          userStatus={userStatus}
          setUserStatus={setUserStatus}
        />
      )}

      {step === 'enterPasscode' && userStatus && (
        <InputPasscode
          userStatus={userStatus}
          passcode={passcode}
          setPasscode={setPasscode}
          password={password}
          setPassword={setPassword}
          loading={liveLessonLoginLoading}
          error={liveLessonLoginError}
          onSubmit={handleSubmitWithPassword}
        />
      )}

      {step === 'chooseActivity' && liveLesson && (
        <ChooseActivity currentLesson={liveLesson} />
      )}
    </>
  );
};
